import { useMemo } from 'react';
import { EMPTY_ITEMS } from '@/components/loop-visualizations/constants';
import type { Item } from '@/lib/api/types/items';
import { useViewItemsQuery } from '@/lib/queries/roadmapping/useViewItemsQuery';

export const useItemsWithParentsForHeaders = ({
  filteredItems,
  allItems,
  enabled = true,
}: {
  filteredItems: Item[];
  allItems: Item[];
  enabled?: boolean;
}): Item[] => {
  const parentIds = useMemo(() => {
    return Array.from(
      new Set(
        filteredItems
          .map((item) => item.parentId)
          .filter((id): id is number => id !== null && id !== undefined),
      ),
    ).sort((a, b) => a - b);
  }, [filteredItems]);

  const missingParentIds = useMemo(() => {
    if (parentIds.length === 0) {
      return [];
    }

    const allItemIds = new Set(allItems.map((item) => item.id));
    return parentIds.filter((id) => !allItemIds.has(id)).sort((a, b) => a - b);
  }, [allItems, parentIds]);

  // Fetch only missing parent items using RoadmapsAPI (avoid a second call when parents already exist)
  const { items } = useViewItemsQuery({
    itemIds: missingParentIds,
    enabled: enabled && missingParentIds.length > 0,
  });

  const parentItems = items ? items : EMPTY_ITEMS;

  // Combine allItems with parent items for complete dataset
  return useMemo(() => {
    const itemsMap = new Map(allItems.map((item) => [item.id, item]));

    for (const item of parentItems) {
      if (!itemsMap.has(item.id)) {
        itemsMap.set(item.id, item);
      }
    }

    return Array.from(itemsMap.values());
  }, [allItems, parentItems]);
};
